// tekno3 "Review this scene" (spec 2026-10-10 §9): POST /api/deepagent/review with the playhead
// second, a canvas screenshot at it and the scene's current (possibly unsaved) state. Resolves to the
// mw report or { error: code } — never throws. Ids are validated before they reach a URL.
import { authedFetch } from "../../../common/http_service";
import { getEditorPlayhead } from "../../../common/playhead_tracker";
import { FPS } from "../../../common/sceneTimeline.es6";
import { getDescendantBoxes } from "../../../common/utils";
import { captureSceneAt } from "../../utils/sceneCapture";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NAV = /^pa-[A-Za-z0-9-]{3,80}$/;
export const REVIEW_MAX_BYTES = 2000000; // mw body limit (spec §12): the screenshot goes first
const TIMEOUT_MS = 90000; // mw answers within 60 s (full review); slack for the upload
const root = () =>
  String(window.ideaLibraryRootUrl || "")
    .replace(/\/+$/, "")
    .replace(/\/api$/, "");
const arr = (v) => (Array.isArray(v) ? v : []);
const bytes = (s) => new Blob([s]).size;
const errorCode = (status) =>
  status === 429
    ? "busy"
    : status === 413
      ? "tooLarge"
      : status === 403 || status === 404
        ? "notFound"
        : "failed";

/** The scene's own boxes (+ descendants), toolbars, nav item, view toolbar (background) and sounds. */
export function sceneSnapshot(present, navId) {
  const navItem = present.navItemsById[navId];
  const boxesById = {};
  const pluginToolbarsById = {};
  (navItem.boxes || []).forEach((id) => {
    const box = present.boxesById[id];
    if (!box) return;
    [id, ...getDescendantBoxes(box, present.boxesById)].forEach((b) => {
      if (present.boxesById[b]) boxesById[b] = present.boxesById[b];
      if (present.pluginToolbarsById && present.pluginToolbarsById[b])
        pluginToolbarsById[b] = present.pluginToolbarsById[b];
    });
  });
  return {
    boxesById,
    pluginToolbarsById,
    navItem,
    viewToolbar: (present.viewToolbarsById && present.viewToolbarsById[navId]) || null,
    soundList: (present.soundList && present.soundList[navId]) || [],
  };
}

export async function requestReview({ trainingId, navId, quick = false }, getState) {
  if (!UUID.test(String(trainingId || "")) || !NAV.test(String(navId || "")))
    return { error: "notFound" };
  const present = getState().undoGroup.present;
  if (!present.navItemsById || !present.navItemsById[navId]) return { error: "notFound" };
  const p = getEditorPlayhead();
  const playheadSec =
    p && String(p.sceneId) === String(navId) ? +(p.frame / FPS).toFixed(2) : undefined;
  const body = { trainingId, navId, sceneSnapshot: sceneSnapshot(present, navId) };
  if (playheadSec !== undefined) body.playheadSec = playheadSec;
  if (quick) body.quick = true;
  else {
    const shot = await captureSceneAt(navId, playheadSec || 0);
    if (shot) body.screenshot = shot;
  }
  let json = JSON.stringify(body);
  if (bytes(json) > REVIEW_MAX_BYTES && body.screenshot) {
    delete body.screenshot;
    json = JSON.stringify(body);
  }
  if (bytes(json) > REVIEW_MAX_BYTES) return { error: "tooLarge" };
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const r = await authedFetch(
      `${root()}/api/deepagent/review`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: json,
        signal: ctrl.signal,
      },
      { quiet: true }
    );
    if (!r.ok) return { error: errorCode(r.status) };
    const d = (await r.json()) || {};
    return {
      issues: arr(d.issues),
      fixes: arr(d.fixes),
      frames: arr(d.frames),
      summary: typeof d.summary === "string" ? d.summary : "",
    };
  } catch (e) {
    return { error: ctrl.signal.aborted ? "timeout" : "failed" };
  } finally {
    clearTimeout(timer);
  }
}
